import { Validation } from './Validation';
import CategoryTypeJson from './CategoryTypeJson';
import PriceJson from './PriceJson';

export default interface PriceTemplateJson {
   ID: number | null,

   /**
    * Name der Preis Vorlage.
    */
   title: string,

   /**
    * Name des Preises.
    */
   priceTitle: string,

   /**
    * Besondere Hinweise, die für diesen Preis oder diese Vergünstigung gelten. Kann ein leerer String sein wenn nichts besonderes gilt.
    */
   priceRemarks: string,

   /**
     * Wenn true, wird der Preis nur für die Verwaltung angezeigt.
     */
   priceIsManagementOnly: boolean,

   /**
     * Ist ein Preis auf Anfrage, ist das auf true gesetzt. Dann soll das Frontend bitte den obigen "price" nicht anzeigen. Er existiert aber trotzdem schon, damit die Verwaltung was vorausgefüllt haben kann.
     */
   priceIsOnRequestOnly: boolean,

   /**
     * Gibt an für welche Typen von Kunden diese Bedingungen gelten. Wenn der type null ist, kann jeder (auch ohne Account) diese Bedingungen buchen
     */
   priceRestrictToTypes: CategoryTypeJson[] | null,

   /**
    * Gibt an, ob die Stornierungsgebühr pro Teilnehmer gilt.
    */
   priceCancellationFeePerParticipant: boolean,

   /**
    * Stornierungsgebuer in Eurocent.
    */
   priceCancellationFee: number,

   /**
     * Gibt an, wie welcher Anteil an Mehrwertsteuer in der Stornierungsgebuer enthalten ist.  0-100%
     */
   priceCancellationFeeIncludedVATRate: number,

   /**
    * Stornierungsbedingungen.
    */
   priceCancellationRemarks: string,

   /**
    * Preis pro Teilnehmer für diese Veranstaltung in Eurocent der als Anzahlung fällig ist.
    */
   priceDeposit: number,

   /**
    * Betrag zu dem diese Veranstaltung angeboten wird in Eurocent. Gilt für alle weiteren Teilnehmenden.
    */
   priceDepositAdditionalParticipant: number,

   /**
    * Teilnehmerzahl bis zu der priceDeposit gilt. Bei null wird immer der priceDeposit berechnet.
    */
   priceDepositParticipantCount: number | null,

   /**
     * Gibt an, wie welcher Anteil an Mehrwertsteuer in den Anzahlungen enthalten ist. 0-100%
     */
   priceDepositIncludedVATRate: number,

   /**
    * Preis pro Teilnehmer für dieses Programm in Eurocent der nicht als Anzahlung fällig ist. 
    * Der Gesamtpreis des Kurses pro Teilnehmer ist priceDeposit + priceNonDeposit
    */
   priceNonDeposit: number,

   /**
    * Restbetrag zu dem diese Veranstaltung angeboten wird in Eurocent. Gilt für alle weiteren Teilnehmenden.
    */
   priceNonDepositAdditionalParticipant: number,

   /**
    * Teilnehmerzahl bis zu der priceNonDeposit gilt. Bei null wird immer der priceNonDeposit berechnet.
    */
   priceNonDepositParticipantCount: number | null,

   /**
     * Gibt an, wie welcher Anteil an Mehrwertsteuer in den Restbetraegen enthalten ist.  0-100%
     */
   priceNonDepositIncludedVATRate: number,

   /**
    * Gibt für das Kunden-Buchungsformular an, in welche Klasse die Teilnehmenden für den Preis mindestens gehen müssen. Kann null sein, falls keine Einschränkung besteht.
    */
   priceParticipantClassMinimum: number | null,

   /**
    * Gibt für das Kunden-Buchungsformular an, in welche Klasse die Teilnehmenden für den Preis maximal gehen dürfen. Kann null sein, falls keine Einschränkung besteht.
    */
   priceParticipantClassMaximum: number | null,

   /**
    * Gibt an, dass dieser Tarif nur verfügbar ist, wenn alle Erziehnungsberechtigten berufstätig sind.
    */
   priceRequiresAllGuardiansEmployed: boolean,

   /**
     * Gibt an, dass zusätzliche Angaben zwingend erforderlich sind (realisiert durch zusätzliches Textfeld, welches dann Pflicht ist).
     */
   priceRequiresAdditionalInfo: boolean,
}

export function defaultPriceTemplateJson(): PriceTemplateJson {
   return {
      ID: null,
      title: "",
      priceTitle: "",
      priceRemarks: "",
      priceIsManagementOnly: false,
      priceIsOnRequestOnly: false,
      priceRestrictToTypes: null,
      priceCancellationFeePerParticipant: false,
      priceCancellationFee: 0,
      priceCancellationFeeIncludedVATRate: 0,
      priceCancellationRemarks: "",
      priceDeposit: 0,
      priceDepositAdditionalParticipant: 0,
      priceDepositParticipantCount: null,
      priceDepositIncludedVATRate: 0,
      priceNonDeposit: 0,
      priceNonDepositAdditionalParticipant: 0,
      priceNonDepositParticipantCount: null,
      priceNonDepositIncludedVATRate: 0,
      priceParticipantClassMinimum: null,
      priceParticipantClassMaximum: null,
      priceRequiresAllGuardiansEmployed: false,
      priceRequiresAdditionalInfo: false,
   };
}

export function getPriceJsonFromTemplateJson(json: PriceTemplateJson): PriceJson {
  return {
    ...defaultPriceTemplateJson(),
    title: json.priceTitle,
    remarks: json.priceRemarks,
    isManagementOnly: json.priceIsManagementOnly,
    isOnRequestOnly: json.priceIsOnRequestOnly,
    restrictToTypes: json.priceRestrictToTypes ? [ ...json.priceRestrictToTypes ] : null,
    cancellationFeePerParticipant: json.priceCancellationFeePerParticipant,
    cancellationFee: json.priceCancellationFee,
    cancellationFeeIncludedVATRate: json.priceCancellationFeeIncludedVATRate,
    cancellationRemarks: json.priceCancellationRemarks,
    priceDeposit: json.priceDeposit,
    priceDepositAdditionalParticipant: json.priceDepositAdditionalParticipant,
    priceDepositParticipantCount: json.priceDepositParticipantCount,
    priceDepositIncludedVATRate: json.priceDepositIncludedVATRate,
    priceNonDeposit: json.priceNonDeposit,
    priceNonDepositAdditionalParticipant: json.priceNonDepositAdditionalParticipant,
    priceNonDepositParticipantCount: json.priceNonDepositParticipantCount,
    priceNonDepositIncludedVATRate: json.priceNonDepositIncludedVATRate,
    participantClassMinimum: json.priceParticipantClassMinimum,
    participantClassMaximum: json.priceParticipantClassMaximum,
    requiresAllGuardiansEmployed: json.priceRequiresAllGuardiansEmployed,
    requiresAdditionalInfo: json.priceRequiresAdditionalInfo,
  };
}

export function validatePriceTemplateJsonAddNew(item: PriceTemplateJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateNonEmptyString("title", 100)
      .validateNonEmptyString("priceTitle", 100)
      .validateString("priceRemarks", 500)
      .validateInteger("priceCancellationFee", 0)
      .validateNumber("priceCancellationFeeIncludedVATRate", 0, 100)
      .validateString("priceCancellationRemarks", 500)
      .validateInteger("priceDeposit", 0)
      .validateInteger("priceDepositAdditionalParticipant", 0)
      .validateInteger("priceDepositParticipantCount", 0, null, true)
      .validateNumber("priceDepositIncludedVATRate", 0, 100)
      .validateInteger("priceNonDeposit", 0)
      .validateInteger("priceNonDepositAdditionalParticipant", 0)
      .validateInteger("priceNonDepositParticipantCount", 0, null, true)
      .validateNumber("priceNonDepositIncludedVATRate", 0, 100)
      .validateInteger("priceParticipantClassMinimum", 0, 120, true)
      .validateInteger("priceParticipantClassMaximum", 0, 120, true)
      .finish();
}

export function validatePriceTemplateJson(item: PriceTemplateJson) {
   return new Validation(item)
      .validateInteger("ID", 0, null, true)
      .validateNonEmptyString("title", 100)
      .validateNonEmptyString("priceTitle", 100)
      .validateString("priceRemarks", 500)
      .validateInteger("priceCancellationFee", 0)
      .validateNumber("priceCancellationFeeIncludedVATRate", 0, 100)
      .validateString("priceCancellationRemarks", 500)
      .validateInteger("priceDeposit", 0)
      .validateInteger("priceDepositAdditionalParticipant", 0)
      .validateInteger("priceDepositParticipantCount", 0, null, true)
      .validateNumber("priceDepositIncludedVATRate", 0, 100)
      .validateInteger("priceNonDeposit", 0)
      .validateInteger("priceNonDepositAdditionalParticipant", 0)
      .validateInteger("priceNonDepositParticipantCount", 0, null, true)
      .validateNumber("priceNonDepositIncludedVATRate", 0, 100)
      .validateInteger("priceParticipantClassMinimum", 0, 120, true)
      .validateInteger("priceParticipantClassMaximum", 0, 120, true)
      .finish();
}